'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { FileText, Plus, Receipt, User } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import type { CreateAction } from './useCreateActions';

const icons = { 'new-customer': User, 'new-estimate': FileText, 'new-invoice': Receipt, 'log-request': Plus };

export function CreateMenu({ actions, onSelect, compact = false, onOpen }: {
    actions: CreateAction[];
    onSelect: (_action: CreateAction) => void;
    compact?: boolean;
    onOpen: () => void;
}) {
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const optionsId = useId();

    useEffect(() => {
        if (!open) return;
        containerRef.current?.querySelector<HTMLButtonElement>('[data-create-option]')?.focus();
        const closeOutside = (event: PointerEvent) => {
            if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
        };
        document.addEventListener('pointerdown', closeOutside);
        return () => document.removeEventListener('pointerdown', closeOutside);
    }, [open]);

    if (!actions.length) return null;

    return (
        <div ref={containerRef} className="relative" onKeyDown={event => {
            if (event.key === 'Escape') {
                event.stopPropagation();
                setOpen(false);
                triggerRef.current?.focus();
            }
        }}>
            <Button
                ref={triggerRef}
                variant="infoSolid"
                size={compact ? 'icon' : 'md'}
                className={compact ? 'min-h-[44px] min-w-[44px]' : undefined}
                aria-label="Create"
                aria-expanded={open}
                aria-controls={open ? optionsId : undefined}
                onClick={event => {
                    event.stopPropagation();
                    setOpen(!open);
                    if (!open) onOpen();
                }}
            >
                <Plus className="w-4 h-4" aria-hidden="true" />
                <span className={compact ? 'sr-only' : ''}>Create</span>
            </Button>
            {open && (
                <div id={optionsId} role="group" aria-label="Create options" className="absolute right-0 top-full mt-2 w-56 bg-bg-surface border border-border-subtle rounded-xl shadow-xl z-[60] overflow-hidden py-1">
                    {actions.map(action => {
                        const Icon = icons[action.id];
                        return (
                            <Button key={action.id} data-create-option className="w-full min-h-[44px] justify-start rounded-none px-4 text-zinc-300" onClick={() => {
                                setOpen(false);
                                onSelect(action);
                            }}>
                                <Icon className="w-4 h-4 text-zinc-400" aria-hidden="true" />
                                {action.title}
                            </Button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
